import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logger from '../../utils/logging/logger';
import { clearSavedGuestDetails as clearSavedDetails } from './paymentForm/guestPaymentApi';
import EnrollmentBrand from './EnrollmentBrand';
import { Container, Card, Title, Subtitle, ErrorMessage, PageSpinner, Button } from './EnrollmentFlow.styles';

const PROCESSING_MESSAGE = 'Your payment is still processing. Once it is confirmed we will email you, and your enrollment will be ready. There is no need to pay again.';

// Stripe's return_url for guest checkout. Finalizes enrollment, then signs the
// brand-new student in (the app then prompts them to set a password).
function GuestCheckoutComplete() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [error, setError] = useState('');
  // 'processing' = the bank has not confirmed yet (not a failure); 'failed' = back to the catalog to retry.
  const [outcome, setOutcome] = useState('error');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const paymentIntentId = searchParams.get('payment_intent');
    const clientSecret = searchParams.get('payment_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    if (!paymentIntentId || !clientSecret) {
      setError('This confirmation link is incomplete.');
      return;
    }
    if (redirectStatus === 'processing') {
      setOutcome('processing');
      setError(PROCESSING_MESSAGE);
      return;
    }
    if (redirectStatus && redirectStatus !== 'succeeded') {
      setOutcome('failed');
      setError('Your payment was not completed. You have not been charged.');
      return;
    }

    (async () => {
      try {
        const result = await api.guestCompleteCheckout(paymentIntentId, clientSecret);
        clearSavedDetails();
        if (result.token && result.user) {
          applySession(result);
          navigate(`/confirmation?payment_intent=${encodeURIComponent(paymentIntentId)}`, { replace: true });
        } else {
          // Account already existed (or the one-time sign-in was used): the
          // enrollment is done, they just sign in.
          navigate('/login', { replace: true });
        }
      } catch (e) {
        logger.error('Guest checkout completion failed', { error: e });
        if (e?.errorData?.status === 'processing') {
          setOutcome('processing');
          setError(PROCESSING_MESSAGE);
          return;
        }
        setError(e.message || 'We could not finalize your enrollment. If you were charged, please contact support.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Card>
        <EnrollmentBrand />
        {error ? (
          <>
            <ErrorMessage><FiAlertCircle />{error}</ErrorMessage>
            {outcome === 'failed' ? (
              <Button type="button" onClick={() => navigate('/catalog')}>Back to the course catalog to try again</Button>
            ) : (
              <Button type="button" onClick={() => navigate('/login')}>Go to sign in</Button>
            )}
          </>
        ) : (
          <>
            <PageSpinner />
            <Title>Finishing your enrollment</Title>
            <Subtitle>Please don&apos;t close this page...</Subtitle>
          </>
        )}
      </Card>
    </Container>
  );
}

export default GuestCheckoutComplete;
